import {Card} from 'platform/components';
import {Box, Stack, Show, VStack} from 'platform/foundation';

import {useMemo} from 'react';
import {useSelector} from 'react-redux';

import {indexBy, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetVehicleV2Query} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {selectSaleVehicle} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {usePermissions} from '@dms/shared';
import {
  FeatureMarketAnalysisCard,
  selectFeatureCategories,
  selectInteriorMaterials,
  selectVehicleFeatures,
  VehicleCatalogueEnumOption,
} from '@dms/teas';

import {composePath, parseDate, useNavigate} from 'shared/web';

import {FeaturesDetail} from './components/FeaturesDetail';

interface VehicleFeaturesProps {
  vehicleId: string;
}

export function VehicleFeatures(props: VehicleFeaturesProps) {
  const navigate = useNavigate();

  const {data: saleVehicle} = useSelector(selectSaleVehicle(props.vehicleId));
  const {data: vehicleDetail} = useGetVehicleV2Query(
    {vehicleId: props.vehicleId},
    {skip: isNil(props.vehicleId)}
  );

  const {data: vehicleParticipation} = useGetParticipationQuery({
    recordId: props.vehicleId,
    resourceId: EntityResourceIds.vehicle,
  });

  const [canUpdateVehicle, canSetCustomVehicleId, canReadBusinessCase, canReadServiceCase] =
    usePermissions({
      permissionKeys: [
        'updateVehicle',
        'setCustomIdVehicle',
        'businessCaseRead',
        'serviceCaseRead',
      ],
      scopes: {
        updateVehicle: {
          participation: vehicleParticipation,
          branchId: saleVehicle?.branchId,
        },
        setCustomIdVehicle: {
          participation: vehicleParticipation,
          branchId: saleVehicle?.branchId,
        },
      },
    });
  const canEditVehicle = canUpdateVehicle && canSetCustomVehicleId;

  const categories = useSelector(selectFeatureCategories(vehicleDetail?.type));
  const features = useSelector(selectVehicleFeatures(vehicleDetail?.type));
  const interiorMaterials = useSelector(selectInteriorMaterials(vehicleDetail?.type));
  const flattenedFeatures = useMemo(() => flattenFeatures(features), [features]);
  const flattenedFeaturesById = indexBy((item) => item.value, flattenedFeatures);

  const vehicleYear: number | null =
    (isNotNil(vehicleDetail?.firstRegistrationOn)
      ? parseDate(vehicleDetail?.firstRegistrationOn).getFullYear()
      : null) ??
    (isNotNil(vehicleDetail?.manufacturedOn)
      ? parseDate(vehicleDetail?.manufacturedOn).getFullYear()
      : null);

  const vehicleFeatures = useMemo(() => {
    if (isNil(vehicleDetail?.features)) {
      return [];
    }

    const selectedFeatureKeys = vehicleDetail.features.map((f) => f.key);
    const parentsToHide = selectedFeatureKeys.flatMap((key) => findParentFeatures(key, features));

    return vehicleDetail.features
      .filter((vehicleFeature) => !parentsToHide.includes(vehicleFeature.key))
      .map((vehicleFeature) => ({
        ...flattenedFeaturesById[vehicleFeature.key],
        isHighlighted: vehicleFeature.isHighlighted,
      }));
  }, [flattenedFeaturesById, vehicleDetail?.features, features]);

  const analysedFeatureDefinitions = [
    ...flattenFeatures(features),
    ...interiorMaterials.map((item) => ({
      value: item.value,
      label: item.label,
    })),
  ];

  const analysedFeatures = useMemo(() => {
    const result = vehicleDetail?.features?.map((feature) => feature.key) ?? [];

    // Interior material pseudo-feature value (workaround based on Alpha implementation)
    const interiorMaterial = vehicleDetail?.interiorMaterial;
    if (isNotNil(interiorMaterial)) {
      result.push(interiorMaterial);
    }

    return result;
  }, [vehicleDetail?.features, vehicleDetail?.interiorMaterial]);

  const handleOnClick = () => {
    if (!props.vehicleId) {
      return;
    }

    return navigate(
      composePath(vehiclesRoutes.editV2Tab, {
        params: {id: props.vehicleId, tab: 'features'},
      })
    );
  };

  return (
    <Stack direction={['column-reverse', 'column-reverse', 'row', 'row']} spacing={4}>
      <Box flex={1}>
        <Card
          title={i18n.t('general.labels.allFeatures')}
          actions={
            canEditVehicle
              ? [
                  {
                    type: 'button',
                    variant: 'link',
                    leftIcon: 'image/edit',
                    onClick: handleOnClick,
                    'data-testid': testIds.vehicles.features('allFeatures-edit'),
                    title: i18n.t('general.actions.edit'),
                  },
                ]
              : undefined
          }
        >
          <FeaturesDetail
            vehicleId={props.vehicleId}
            categories={categories}
            features={vehicleFeatures}
            addFeaturesAction={{
              title: i18n.t('entity.vehicle.actions.addFeatures'),
              onClick: handleOnClick,
              isDisabled: !canEditVehicle,
            }}
          />
        </Card>
      </Box>
      <Show when={!!saleVehicle}>
        <Show when={canReadBusinessCase}>
          <VStack width={['100%', '100%', 80, 100]} spacing={4}>
            <Show when={saleVehicle?.id}>
              <FeatureMarketAnalysisCard
                data-testid={testIds.vehicles.features('features')}
                allFeatures={analysedFeatureDefinitions}
                vehicleFeatures={analysedFeatures}
                vehicleMake={vehicleDetail?.make ?? ''}
                vehicleModel={vehicleDetail?.modelFamily ?? ''}
                vehicleYear={vehicleYear}
              />
            </Show>
          </VStack>
        </Show>
      </Show>
      <Show when={!saleVehicle && canReadServiceCase}>
        <VStack width={['100%', '100%', 80, 100]} spacing={4}>
          <FeatureMarketAnalysisCard
            data-testid={testIds.vehicles.features('features')}
            allFeatures={analysedFeatureDefinitions}
            vehicleFeatures={analysedFeatures}
            vehicleMake={vehicleDetail?.make ?? ''}
            vehicleModel={vehicleDetail?.modelFamily ?? ''}
            vehicleYear={vehicleYear}
          />
        </VStack>
      </Show>
    </Stack>
  );
}

function flattenFeatures(
  featureItems: readonly VehicleCatalogueEnumOption[]
): VehicleCatalogueEnumOption[] {
  return featureItems.flatMap((item) => {
    const {children, ...itemWithoutChildren} = item;
    return [...flattenFeatures(children || []), itemWithoutChildren];
  });
}

function findParentFeatures(
  featureKey: string,
  allFeatures: readonly VehicleCatalogueEnumOption[]
): string[] {
  const searchInFeatures = (features: readonly VehicleCatalogueEnumOption[]): string[] =>
    features.flatMap((feature) => {
      const isParentOfTarget = feature.children?.some(
        (child) =>
          child.value === featureKey ||
          child.children?.some((grandChild) => grandChild.value === featureKey)
      );

      return [
        ...(isParentOfTarget ? [feature.value] : []),
        ...(feature.children ? searchInFeatures(feature.children) : []),
      ];
    });

  return searchInFeatures(allFeatures);
}
